const currencyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  useGrouping: false,
});

export const formatCurrency = (value) => {
  const amount = Number(value);
  return currencyFormatter.format(Number.isFinite(amount) ? amount : 0);
};

export const formatName = (...parts) =>
  parts.filter((part) => typeof part === "string" && part.trim()).join(" ") ||
  "Not provided";

export const formatCityStateZip = (city, state, zipcode) => {
  const cityValue = city?.trim();
  const stateZip = [state?.trim(), zipcode?.toString().trim()]
    .filter(Boolean)
    .join(" ");

  return [cityValue, stateZip].filter(Boolean).join(", ");
};

export const formatAddressLines = ({
  addressLine1,
  addressLine2,
  attention,
  city,
  state,
  zipcode,
  country,
}) => {
  const countryValue = country?.trim();
  const normalizedCountry =
    countryValue?.length <= 3 ? countryValue.toUpperCase() : countryValue;
  const lines = [
    attention?.trim(),
    addressLine1?.trim(),
    addressLine2?.trim(),
    formatCityStateZip(city, state, zipcode),
    normalizedCountry,
  ].filter(Boolean);

  return lines.length ? lines : ["Not provided"];
};

const normalizeImageUrl = (value) => {
  if (typeof value !== "string") return "";

  const normalizedValue = value.trim();
  return normalizedValue && normalizedValue.toLowerCase() !== "null"
    ? normalizedValue
    : "";
};

export const getReceiptItems = (recipient) =>
  (recipient?.order_products || []).map((item) => ({
    ...item,
    itemType: item.product_type ? "Add-on" : "Product",
    receiptImage: normalizeImageUrl(item.variation_image),
  }));

export const getReceiptTotals = (order) => {
  const replacementOrder = Number(order?.order_type_identifier) === 1;
  const totals = [
    { label: "Subtotal", value: order?.sub_total },
  ];

  if (replacementOrder && Number(order?.replacement_discount_amount) > 0) {
    totals.push({
      label: "Replacement discount",
      value: -Number(order.replacement_discount_amount),
    });
  } else if (Number(order?.discount_price) > 0) {
    totals.push({
      label: order?.promocode
        ? `Discount (${order.promocode})`
        : "Discount",
      value: -Number(order.discount_price),
    });
  }

  if (Number(order?.subscription_discount) > 0) {
    totals.push({
      label: "Subscription discount",
      value: -Number(order.subscription_discount),
    });
  }

  totals.push(
    { label: "Delivery", value: order?.delivery_charges },
    { label: "Upcharge", value: order?.upcharge_amount },
    { label: "Tax", value: order?.tax }
  );

  if (Number(order?.petal_rewards_point_amount) > 0) {
    totals.push({
      label: "Rewards redemption",
      value: -Number(order.petal_rewards_point_amount),
    });
  }

  if (Number(order?.gift_card_amount) > 0) {
    totals.push({
      label: "Gift card",
      value: -Number(order.gift_card_amount),
    });
  }

  return totals;
};

export const humanizeStatus = (status) =>
  status
    ? status
        .split("_")
        .filter(Boolean)
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(" ")
    : "Not provided";

export const getPaymentDetail = (order) => {
  const transactions =
    order?.order_transaction?.order_payment_transactions || [];
  const transaction =
    transactions.find((item) => Number(item?.order_id) === 0) ||
    transactions[0];

  if (!transaction) return "Payment information not provided";

  const type = humanizeStatus(transaction.transaction_type);
  const cardType = transaction.card_type?.trim();
  const lastFour = transaction.card_last_4_digits?.toString().trim();
  const cardDetail = [cardType, lastFour ? `ending in ${lastFour}` : ""]
    .filter(Boolean)
    .join(" ");

  return [type, cardDetail].filter(Boolean).join(" - ");
};

export const getStoreContact = (store) => ({
  title: store?.title?.trim() || "French Florist",
  phone: store?.local_phone1?.trim() || "",
  email: store?.email_id_main?.trim() || "info@frenchflorist.com",
});

export const getReceiptFileName = (order) => {
  const orderNumber = String(order?.order_number || order?.id || "receipt")
    .replace(/[^a-z0-9_-]+/gi, "-")
    .replace(/^-+|-+$/g, "");

  return `French-Florist-Order-${orderNumber || "receipt"}.pdf`;
};
